import * as React from "react";
import {Provider as ReduxProvider} from "react-redux";
import {store} from "@/store";
import MainFrame from "../MainFrame";
import {TranslationProvider} from "@/context/TranslationProvider";
import BackendErrorDialog from "@/containers/BackendErrorDialog";
import {AuthProvider, useAuth} from "@/context/AuthProvider";
import {StructureConfigProvider} from "@/context/StructureConfigProvider";
import {RelatedClientProvider} from "@/context/RelatedClientProvider";
import {Login} from "@/context/Login";
import {DefaultThemeProvider} from "@/context/ThemeProvider";
import {useTranslations} from "@/context/TranslationProvider";
import LoadingScreen from "../LoadingScreen";
import DatePickerLocalizationProvider from "@/context/DatePickerLocalizationProvider";
import ClientProvider from "@/apollo-client/ClientProvider";
import {defineMessage} from "react-intl";
import {ErrorMessagesProvider} from "@dvrt/graphql-widgets";
import errorMessages from "@/utils/error-messages";

/// we are in loading state until authorization info and translations are received
const Loading = ({children}: {children: React.ReactNode}) => {
    const auth = useAuth();
    const translations = useTranslations();

    if (!translations.hasMessages()) return <LoadingScreen />;

    if (!auth)
        return (
            <LoadingScreen
                messageId={defineMessage({
                    id: "loading.authorization",
                    defaultMessage: "Getting authorization information",
                })}
            />
        );

    return <>{children}</>;
};

export default () => {
    return (
        <ReduxProvider store={store}>
            <TranslationProvider>
                <ErrorMessagesProvider messages={errorMessages}>
                    <DefaultThemeProvider>
                        <BackendErrorDialog />
                        <Login>
                            <StructureConfigProvider>
                                <ClientProvider>
                                    <AuthProvider>
                                        <RelatedClientProvider>
                                            <DatePickerLocalizationProvider>
                                                <Loading>
                                                    <MainFrame />
                                                </Loading>
                                            </DatePickerLocalizationProvider>
                                        </RelatedClientProvider>
                                    </AuthProvider>
                                </ClientProvider>
                            </StructureConfigProvider>
                        </Login>
                    </DefaultThemeProvider>
                </ErrorMessagesProvider>
            </TranslationProvider>
        </ReduxProvider>
    );
};
